import { useCallback } from "react";
import { useLocalStorageObject, useLocalStorage } from "./useLocalStorage";
import { STORAGE_KEYS, UserPreferences } from "@/lib/localStorage";

/**
 * User preferences hook with localStorage persistence
 */
export function useUserPreferences() {
  // Default preferences - Dark mode disabled, light mode only
  const defaultPreferences: UserPreferences = {
    notifications: true,
    darkMode: false, // Always false - light mode only
    language: "vi",
    autoSave: true,
    showOnlineStatus: true,
  };

  const [preferences, updatePreferences, resetPreferences] =
    useLocalStorageObject(STORAGE_KEYS.USER_PREFERENCES, defaultPreferences);

  const [language, setLanguage] = useLocalStorage(STORAGE_KEYS.LANGUAGE, "vi");

  /**
   * Toggle notification settings
   */
  const toggleNotifications = useCallback(() => {
    updatePreferences({ notifications: !preferences.notifications });
  }, [preferences.notifications, updatePreferences]);

  /**
   * Toggle dark mode - DISABLED (Light mode only)
   */
  const toggleDarkMode = useCallback(() => {
    // Dark mode is disabled - always use light mode
    updatePreferences({ darkMode: false });
    document.documentElement.classList.remove("dark");
  }, [updatePreferences]);

  /**
   * Change language
   */
  const changeLanguage = useCallback(
    (newLanguage: "vi" | "en") => {
      setLanguage(newLanguage);
      updatePreferences({ language: newLanguage });
    },
    [setLanguage, updatePreferences],
  );

  /**
   * Toggle auto-save for drafts
   */
  const toggleAutoSave = useCallback(() => {
    updatePreferences({ autoSave: !preferences.autoSave });
  }, [preferences.autoSave, updatePreferences]);

  /**
   * Toggle online status visibility
   */
  const toggleOnlineStatus = useCallback(() => {
    updatePreferences({ showOnlineStatus: !preferences.showOnlineStatus });
  }, [preferences.showOnlineStatus, updatePreferences]);

  /**
   * Reset all preferences to default
   */
  const resetToDefaults = useCallback(() => {
    resetPreferences();
    setLanguage("vi");
    // Ensure light mode only
    document.documentElement.classList.remove("dark");
  }, [resetPreferences, setLanguage]);

  /**
   * Get preference value by key
   */
  const getPreference = useCallback(
    <K extends keyof UserPreferences>(key: K): UserPreferences[K] => {
      return preferences[key] as UserPreferences[K];
    },
    [preferences],
  );

  /**
   * Set specific preference
   */
  const setPreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      updatePreferences({ [key]: value } as Partial<UserPreferences>);
    },
    [updatePreferences],
  );

  /**
   * Initialize preferences on app start
   */
  const initializePreferences = useCallback(() => {
    // Force light mode only - remove any dark class
    document.documentElement.classList.remove("dark");
    updatePreferences({ darkMode: false });

    // Set language
    if (preferences.language && typeof preferences.language === "string") {
      setLanguage(preferences.language);
    }

    return preferences;
  }, [preferences, setLanguage, updatePreferences]);

  return {
    // State
    preferences,
    language,

    // Individual preference getters
    notifications: preferences.notifications,
    darkMode: preferences.darkMode,
    autoSave: preferences.autoSave,
    showOnlineStatus: preferences.showOnlineStatus,

    // Actions
    toggleNotifications,
    toggleDarkMode,
    changeLanguage,
    toggleAutoSave,
    toggleOnlineStatus,
    resetToDefaults,

    // Generic setters/getters
    getPreference,
    setPreference,
    updatePreferences,

    // Initialization
    initializePreferences,
  };
}

/**
 * Hook for managing last visited page
 */
export function useLastVisitedPage() {
  const [lastPage, setLastPage] = useLocalStorage(
    STORAGE_KEYS.LAST_VISITED_PAGE,
    "/feed",
  );

  const updateLastVisitedPage = useCallback(
    (page: string) => {
      // Don't save login/onboarding pages as last visited
      if (page !== "/login" && page !== "/onboarding" && page !== "/") {
        setLastPage(page);
      }
    },
    [setLastPage],
  );

  return {
    lastPage,
    updateLastVisitedPage,
  };
}

/**
 * Hook for managing app version and updates
 */
export function useAppVersion() {
  const [appVersion, setAppVersion] = useLocalStorage(
    STORAGE_KEYS.APP_VERSION,
    "1.0.0",
  );

  const updateAppVersion = useCallback(
    (version: string) => {
      setAppVersion(version);
    },
    [setAppVersion],
  );

  const checkForUpdates = useCallback(() => {
    // Mock version check
    const currentVersion = "1.0.0";
    const hasUpdate = appVersion !== currentVersion;

    if (hasUpdate) {
      updateAppVersion(currentVersion);
    }

    return {
      hasUpdate,
      currentVersion,
      storedVersion: appVersion,
    };
  }, [appVersion, updateAppVersion]);

  return {
    appVersion,
    updateAppVersion,
    checkForUpdates,
  };
}
